import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

/**
 * Lets a page declare where the global Back button should land (its module
 * list page) instead of relying on browser history. Pages call
 * `useBackTo("/sales/orders")`; the global AppSubHeader reads the value and
 * passes it to <BackButton fallback=... />.
 */
interface BackToCtx {
  backTo: string | null;
  register: (path: string | null) => void;
}

const Ctx = createContext<BackToCtx | null>(null);

export const BackToProvider = ({ children }: { children: ReactNode }) => {
  const [backTo, setBackTo] = useState<string | null>(null);
  const register = useCallback((path: string | null) => setBackTo(path), []);
  const value = useMemo(() => ({ backTo, register }), [backTo, register]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useBackToValue = (): string | null => useContext(Ctx)?.backTo ?? null;

/** Declare the module list path the global Back button should navigate to. */
export const useBackTo = (path: string) => {
  const ctx = useContext(Ctx);
  const register = ctx?.register;
  useEffect(() => {
    if (!register) return;
    register(path);
    return () => register(null);
  }, [register, path]);
};
